import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Typography, Divider, Tag } from 'antd';
import { CloudOutlined, WarningOutlined } from '@ant-design/icons';
import { DataCard } from '../common';
import { useTenant } from '../../context/TenantContext';
import { isM365Managed } from '../../utils/customerKey';
import {
  useDattoIdFromCipp,
  useDattoSaasDomains,
} from '../../hooks';
import {
  parseMCPResponse,
} from '../../utils/dattoHelpers';

const { Text } = Typography;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid var(--odin-border);

  &:last-child {
    border-bottom: none;
  }
`;

const MetricLabel = styled(Text)`
  font-size: 14px;
  color: var(--odin-text-secondary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricValue = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${({ $status, theme }) => {
    if ($status === 'critical') return theme.colors.error;
    if ($status === 'warning') return theme.colors.warning;
    return theme.colors.accent;
  }};
`;

const MappingWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing.sm};
  background: var(--odin-status-awaiting-bg);
  border: 1px solid var(--odin-status-awaiting-border);
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: var(--odin-status-awaiting-text);
  font-size: 12px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyStateMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  min-height: 120px;
  text-align: center;
`;

/**
 * DattoSaasWidget - Displays Datto SaaS Protection metrics
 *
 * This component integrates with Datto-MCP to display:
 * - SaaS asset count (protected/unprotected)
 * - Provider badges (M365, Google Workspace, etc.)
 * - Warning states when unprotected assets exist
 *
 * Uses customerMappingService (via useDattoIdFromCipp) to resolve
 * CIPP tenant IDs to Datto client names.
 *
 * Always visible with three-tier rendering:
 * 1. Mapping warning (when no Datto mapping exists)
 * 2. Data display (when mapped and has SaaS assets)
 * 3. Empty state (when mapped but no SaaS assets)
 *
 * @param {Object} props
 * @param {Function} props.onRefresh - Refresh callback
 */
const DattoSaasWidget = ({ onRefresh }) => {
  // Get selected tenant from context (CIPP tenant ID)
  const { selectedTenant } = useTenant();
  const cippTenantId = selectedTenant?.id;

  // Resolve CIPP tenant ID to Datto identifiers using customer mapping service
  const {
    dattoClient,           // Legacy: organizationId (for backward compatibility)
    datto,                 // New: normalized object with organizationId + saasCustomerId
    isResolved,
    isLoading: isMappingLoading,
  } = useDattoIdFromCipp(cippTenantId, {
    enabled: !!cippTenantId,
  });

  const hasDattoMapping = isResolved;

  // Fetch SaaS domains (supports both BCDR organizationId and SaaS-only saasCustomerId)
  const {
    data: domainsData,
    isLoading: domainsLoading,
    error: domainsError,
    refetch,
  } = useDattoSaasDomains(
    {
      organizationId: datto?.organizationId,
      saasCustomerId: datto?.saasCustomerId,
    },
    {
      enabled: hasDattoMapping && (datto?.hasBCDR || datto?.hasSaaS),
    }
  );

  // Calculate SaaS metrics from domains
  const saasMetrics = useMemo(() => {
    const parsed = parseMCPResponse(domainsData);
    const domains = parsed?.domains || [];

    const totalServices = domains.reduce((sum, d) => sum + (d.backupStats?.activeServicesCount || 0), 0);
    const protectedServices = domains.reduce((sum, d) => sum + (d.backupStats?.activeServicesWithRecentBackupCount || 0), 0);

    return {
      total: totalServices,
      protected: protectedServices,
      unprotected: totalServices - protectedServices,
      domains: domains.length,
    };
  }, [domainsData]);

  const domainsByProvider = useMemo(() => {
    const parsed = parseMCPResponse(domainsData);
    const domains = parsed?.domains || [];

    return domains.reduce((acc, domain) => {
      const provider = domain.productType || 'Other';
      if (!acc[provider]) {
        acc[provider] = [];
      }
      acc[provider].push(domain);
      return acc;
    }, {});
  }, [domainsData]);

  // Conditional rendering flags
  const isLoading = isMappingLoading || domainsLoading;
  const showMappingWarning = !isLoading && !hasDattoMapping && cippTenantId;
  const showEmpty = hasDattoMapping && saasMetrics.domains === 0 && !isLoading;

  // Handle refresh
  const handleRefresh = () => {
    if (hasDattoMapping && (datto?.hasBCDR || datto?.hasSaaS)) {
      refetch();
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <DataCard
      title="Datto SaaS Protection"
      icon={CloudOutlined}
      loading={isLoading}
      error={domainsError}
      empty={false}
      onRefresh={handleRefresh}
    >
      {/* Tier 1: Mapping Warning */}
      {showMappingWarning && (
        <MappingWarning>
          <WarningOutlined />
          {isM365Managed(selectedTenant)
            ? 'No Datto mapping found for this tenant. Click "Sync Platforms" to discover.'
            : 'No Datto mapping found for this customer. Add it in Admin → Customer Mappings.'}
        </MappingWarning>
      )}

      {/* Tier 2: Data Display */}
      {hasDattoMapping && !showEmpty && (
        <>
          {/* Protected/Total Metrics */}
          <MetricRow>
            <MetricLabel>
              <CloudOutlined />
              Active Services
            </MetricLabel>
            <MetricValue>
              {saasMetrics.protected}/{saasMetrics.total} backed up
            </MetricValue>
          </MetricRow>

          {/* Not Backed Up Warning (if any) */}
          {saasMetrics.unprotected > 0 && (
            <MetricRow>
              <MetricLabel>
                <WarningOutlined />
                Not Backed Up
              </MetricLabel>
              <MetricValue $status="warning">
                {saasMetrics.unprotected}
              </MetricValue>
            </MetricRow>
          )}

          {/* Provider Badges */}
          {saasMetrics.domains > 0 && (
            <>
              <Divider style={{ margin: '12px 0', borderColor: 'var(--odin-border)' }} />
              <div style={{ marginBottom: 8 }}>
                <Text style={{ fontSize: 12, color: 'var(--odin-text-secondary)' }}>
                  Domains: {saasMetrics.domains}
                </Text>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(domainsByProvider).map(([provider, domains]) => {
                  const tagStyle = provider === 'M365'
                    ? { background: 'var(--odin-status-active-bg)', color: 'var(--odin-status-active-text)', borderColor: 'var(--odin-status-active-border)' }
                    : provider === 'Google Workspace'
                      ? { background: 'var(--odin-status-completed-bg)', color: 'var(--odin-status-completed-text)', borderColor: 'var(--odin-status-completed-border)' }
                      : { background: 'var(--odin-surface-hover)', color: 'var(--odin-text-secondary)', borderColor: 'var(--odin-border)' };
                  return (
                    <Tag key={provider} style={tagStyle}>
                      {provider}: {domains.length}
                    </Tag>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* Tier 3: Empty State */}
      {hasDattoMapping && showEmpty && (
        <EmptyStateMessage>
          <CloudOutlined style={{ fontSize: 48, color: 'var(--odin-text-tertiary)' }} />
          <Text style={{ color: 'var(--odin-text-tertiary)', marginTop: 16 }}>
            No SaaS Protection assets found for this tenant
          </Text>
          <Text style={{ fontSize: 12, color: 'var(--odin-text-tertiary)', marginTop: 8 }}>
            Configure SaaS Protection in Datto to protect Microsoft 365,
            Google Workspace, and other cloud applications
          </Text>
        </EmptyStateMessage>
      )}
    </DataCard>
  );
};

DattoSaasWidget.propTypes = {
  onRefresh: PropTypes.func,
};

export default DattoSaasWidget;
